import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/api.js';
import styles from './UserProfile.module.css';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../Components/ui/Toast.jsx';

const PAYMENT_WINDOW_MS = 15 * 60 * 1000; // 15 minutos

// Countdown por reserva, actualizado cada segundo
const useCountdowns = (travels) => {
  const [ticks, setTicks] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    const hasPending = travels.some(
      t => t.status === 'waiting' && !t.payment_method
    );
    if (hasPending) {
      intervalRef.current = setInterval(() => setTicks(n => n + 1), 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [travels]);

  const getTimeLeft = useCallback((dateStr) => {
    const elapsed = Date.now() - new Date(dateStr).getTime();
    return Math.max(0, PAYMENT_WINDOW_MS - elapsed);
  }, [ticks]); // eslint-disable-line

  return getTimeLeft;
};

const formatCountdown = (ms) => {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60).toString().padStart(2, '0');
  const s = (total % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const UserProfile = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [userData, setUserData] = useState({ nombre: '', correo: '', telefono: '', identificacion: '' });
  const [userTravels, setUserTravels] = useState([]);
  const [tripPassengers, setTripPassengers] = useState({});
  const [expandedTicketId, setExpandedTicketId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ nombre: '', correo: '', telefono: '', identificacion: '' });
  const [editErrors, setEditErrors] = useState({});

  const getTimeLeft = useCountdowns(userTravels);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const meRes = await api.get('/me');
        const userId = meRes.data.id;
        const userRes = await api.get(`/user/${userId}`);
        const u = userRes.data;

        const phoneRaw = u.Number_phone || '';
        const phoneCodeMatch = phoneRaw.match(/^\+\d{1,3}/);
        const phoneCode = phoneCodeMatch ? phoneCodeMatch[0] : '+58';
        const phoneNumber = phoneRaw.replace(/^\+\d{1,3}/, '').trim();

        const fullData = {
          nombre: u.Name || '',
          apellido: u.Last_name || '',
          correo: u.Email || '',
          phoneCode,
          phoneNumber,
          telefono: u.Number_phone || '',
          identificacion: u.Identification || ''
        };
        setUserData({
          ...fullData,
          nombreCompleto: [u.Name, u.Last_name].filter(Boolean).join(' ')
        });
        setEditForm(fullData);

        try {
          const bookingsRes = await api.get('/my_bookings');
          const myBookings = (bookingsRes.data || []).filter(b => b.active !== 0);

          const enrichedTravels = myBookings.map((booking) => {
            const rawTime = booking.departure_time || '';
            return {
              id_ticket: booking.id,
              seat_quant: booking.seat_quant,
              status: booking.Status,
              payment_method: booking.payment_method,
              created_at: booking.date,
              reservation_code: booking.reservation_code,
              tripInfo: {
                id: booking.trip_id,
                date: booking.departure_date || '',
                departing_time: rawTime ? String(rawTime).slice(0, 5) : '—',
                departure: booking.origin || '—',
                arrival: booking.destination || '—',
                price: booking.price_per_seat,
                codigo: booking.reservation_code,
              },
            };
          });
          setUserTravels(enrichedTravels);
        } catch {
          setUserTravels([]);
        }

        setLoading(false);
      } catch {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [navigate]);

  const loadPassengersForTicket = async (ticketId) => {
    try {
      const resp = await api.get(`/booking/${ticketId}`);
      let passengers = [];

      if (resp.data.passengers && Array.isArray(resp.data.passengers) && resp.data.passengers.length > 0) {
        passengers = resp.data.passengers.map(p => ({
          seat_id: p.Seat_number,
          nombre: p.Name || 'No especificado',
          correo: p.Email || '-',
          telefono: p.Phone || '-',
          identificacion: p.Identification || '-',
        }));
      } else if (resp.data.seats && Array.isArray(resp.data.seats)) {
        passengers = resp.data.seats.map(seat => ({
          seat_id: seat.Number_seat || seat.ID,
          nombre: 'Información no disponible',
          correo: '-', telefono: '-', identificacion: '-'
        }));
      }

      setTripPassengers((old) => ({ ...old, [ticketId]: passengers }));
    } catch {
      setTripPassengers((old) => ({ ...old, [ticketId]: [] }));
    }
  };

  const validateEditForm = useCallback(() => {
    const errors = {};
    const nombreTrim = editForm.nombre.trim();
    const apellidoTrim = editForm.apellido.trim();
    const phoneNumberTrim = (editForm.phoneNumber || '').trim();

    if (!nombreTrim) {
      errors.nombre = 'El nombre no puede estar vacío ni contener solo espacios.';
    }
    if (!apellidoTrim) {
      errors.apellido = 'El apellido no puede estar vacío ni contener solo espacios.';
    }
    if (!phoneNumberTrim) {
      errors.phoneNumber = 'El número de teléfono no puede estar vacío ni contener solo espacios.';
    }

    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  }, [editForm]);

  const saveProfileChanges = async () => {
    if (!validateEditForm()) return;

    try {
      const meRes = await api.get('/me');
      const userId = meRes.data.id;
      const fullPhone = `${editForm.phoneCode}${editForm.phoneNumber.trim()}`;
      await api.put(`/user/${userId}`, {
        Name: editForm.nombre.trim(),
        Last_name: editForm.apellido.trim(),
        Number_phone: fullPhone
      });
      setUserData(prev => ({
        ...prev,
        nombre: editForm.nombre.trim(),
        apellido: editForm.apellido.trim(),
        nombreCompleto: `${editForm.nombre.trim()} ${editForm.apellido.trim()}`.trim(),
        phoneCode: editForm.phoneCode,
        phoneNumber: editForm.phoneNumber.trim(),
        telefono: fullPhone
      }));
      showToast('Perfil actualizado correctamente', { type: 'success' });
      setShowEditModal(false);
      setEditErrors({});
    } catch {
      showToast('Error al actualizar el perfil', { type: 'error' });
    }
  };

  const handleEditChange = (key, value) => {
    setEditForm(prev => ({ ...prev, [key]: value }));
    if (editErrors[key]) {
      setEditErrors(prev => ({ ...prev, [key]: undefined }));
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  };

  const getStatusInfo = (status, paymentMethod, timeLeft) => {
    if (status === 'confirm') return { label: 'Pagado', cls: styles.statusPaid };
    if (status === 'cancel')  return { label: 'Cancelado', cls: styles.statusPending };
    if (status === 'waiting' && paymentMethod) return { label: 'En revision', cls: styles.statusWaiting };
    if (status === 'waiting' && !paymentMethod) {
      if (timeLeft > 0) return { label: 'Pendiente de pago', cls: styles.statusWaiting };
      return { label: 'Tiempo agotado', cls: styles.statusPending };
    }
    return { label: 'Pendiente', cls: styles.statusPending };
  };

  if (loading) {
    return (
      <div className={styles.loadingScreen}>
        <div className={styles.spinner}></div>
        <p>Cargando perfil...</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>

      {/* Header */}
      <div className={styles.profileHeader}>
        <div className={styles.avatar}>{getInitials(userData.nombreCompleto)}</div>
        <div className={styles.headerInfo}>
          <h1 className={styles.userName}>{userData.nombreCompleto}</h1>
          <p className={styles.userEmail}>{userData.correo}</p>
        </div>
        <button className={styles.editBtn} onClick={() => { setShowEditModal(true); setEditErrors({}); }}>
          Editar perfil
        </button>
      </div>

      {/* Información personal */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Información Personal</h2>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Nombre completo</span>
            <span className={styles.infoValue}>{userData.nombre || '—'}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Correo electrónico</span>
            <span className={styles.infoValue}>{userData.correo || '—'}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Teléfono</span>
            <span className={styles.infoValue}>{userData.telefono || '—'}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Cédula / Documento</span>
            <span className={styles.infoValue}>{userData.identificacion || '—'}</span>
          </div>
        </div>
      </section>

      {/* Viajes */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          Mis Viajes
          {userTravels.length > 0 && (
            <span className={styles.travelCount}>{userTravels.length}</span>
          )}
        </h2>

        {userTravels.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}></div>
            <p className={styles.emptyTitle}>Sin viajes reservados</p>
            <p className={styles.emptySub}>Cuando realices una reserva aparecerá aquí.</p>
            <button className={styles.bookBtn} onClick={() => navigate('/busroutes')}>
              Buscar viajes
            </button>
          </div>
        ) : (
          <div className={styles.travelsList}>
            {userTravels.map((travel) => {
              const ticketId = travel.id_ticket;
              const isExpanded = expandedTicketId === ticketId;
              const passengers = tripPassengers[ticketId] ?? null;
              const isPendingPayment = travel.status === 'waiting' && !travel.payment_method;
              const timeLeft = isPendingPayment ? getTimeLeft(travel.created_at) : 0;
              const isExpired = isPendingPayment && timeLeft === 0;
              const { label: statusLabel, cls: statusClass } = getStatusInfo(travel.status, travel.payment_method, timeLeft);

              return (
                <div key={ticketId} className={styles.travelCard}>
                  <div className={styles.cardHeader}>
                    <div className={styles.route}>
                      <span className={styles.routeCity}>{travel.tripInfo.departure}</span>
                      <span className={styles.routeArrow}></span>
                      <span className={styles.routeCity}>{travel.tripInfo.arrival}</span>
                    </div>
                    <span className={`${styles.statusBadge} ${statusClass}`}>{statusLabel}</span>
                  </div>

                  <div className={styles.cardBody}>
                    <div className={styles.cardStat}>
                      <span className={styles.cardStatLabel}>Código de reserva</span>
                      <span className={styles.cardStatValue}>{travel.reservation_code || '—'}</span>
                    </div>
                    <div className={styles.cardStat}>
                      <span className={styles.cardStatLabel}>Fecha de salida</span>
                      <span className={styles.cardStatValue}>
                        {travel.tripInfo.date
                          ? (() => { const d = String(travel.tripInfo.date).slice(0,10); const [y,m,day] = d.split('-'); return `${day}/${m}/${y}`; })()
                          : 'Por confirmar'}
                      </span>
                    </div>
                    <div className={styles.cardStat}>
                      <span className={styles.cardStatLabel}>Hora</span>
                      <span className={styles.cardStatValue}>{travel.tripInfo.departing_time}</span>
                    </div>
                    <div className={styles.cardStat}>
                      <span className={styles.cardStatLabel}>Asientos</span>
                      <span className={styles.cardStatValue}>{travel.seat_quant}</span>
                    </div>
                    {travel.payment_method && (
                      <div className={styles.cardStat}>
                        <span className={styles.cardStatLabel}>Método de pago</span>
                        <span className={styles.cardStatValue}>{travel.payment_method}</span>
                      </div>
                    )}
                  </div>

                  {/* Countdown + aviso para reservas pendientes de pago */}
                  {isPendingPayment && (
                    <div className={styles.paymentAlert}>
                      {!isExpired ? (
                        <>
                          <span className={styles.countdown}>
                            Tiempo restante: <strong>{formatCountdown(timeLeft)}</strong>
                          </span>
                          <span className={styles.paymentAlertNote}>
                            Si no pagas a tiempo, el asiento se liberara automaticamente.
                          </span>
                        </>
                      ) : (
                        <span className={styles.expiredNote}>
                          El tiempo de pago ha vencido. Los asientos fueron liberados. Intenta de nuevo.
                        </span>
                      )}
                    </div>
                  )}

                  <div className={styles.cardActions}>
                    {travel.status !== 'cancel' && (
                      <button
                        className={styles.actionBtnOutline}
                        onClick={async () => {
                          if (isExpanded) {
                            setExpandedTicketId(null);
                          } else {
                            setExpandedTicketId(ticketId);
                            if (tripPassengers[ticketId] === undefined) {
                              await loadPassengersForTicket(ticketId);
                            }
                          }
                        }}
                      >
                        {isExpanded ? 'Ocultar detalles' : 'Ver detalles'}
                      </button>
                    )}

                    {/* Botón Volver a pagar (dentro del tiempo) */}
                    {isPendingPayment && !isExpired && (
                      <button className={styles.actionBtnWarning} onClick={() => navigate(`/payment/${ticketId}`)}>
                        Volver a pagar
                      </button>
                    )}

                    {/* Reserva expirada — solo informar, el admin libera desde Ventas */}
                    {isPendingPayment && isExpired && (
                      <span className={styles.expiredAction}>
                        Contacta con soporte para liberar el asiento
                      </span>
                    )}

                    {travel.status === 'confirm' && (
                      <button
                        className={styles.actionBtnGreen}
                        onClick={() => {
                          const selectedSeats = passengers?.length > 0 ? passengers.map(p => p.seat_id) : [];
                          const pricePerSeat = parseFloat(travel.tripInfo.price || 0);
                          localStorage.setItem('currentInvoice', JSON.stringify({
                            bookingId: ticketId,
                            travelDetails: {
                              Origin: travel.tripInfo.departure,
                              Destination: travel.tripInfo.arrival,
                              Departure_date: travel.tripInfo.date,
                              Departure_time: travel.tripInfo.departing_time,
                              price: pricePerSeat,
                              Price: pricePerSeat,
                            },
                            selectedSeats,
                            totalAmount: pricePerSeat * travel.seat_quant,
                            reservationCode: travel.reservation_code,
                            paymentMethod: travel.payment_method,
                          }));
                          navigate('/factura');
                        }}
                      >
                        Ver ticket
                      </button>
                    )}
                  </div>

                  {isExpanded && (
                    <div className={styles.expandedDetails}>
                      <div className={styles.detailsGrid}>
                        <div className={styles.detailItem}>
                          <span className={styles.detailLabel}>Precio por asiento</span>
                          <span className={styles.detailValue}>${parseFloat(travel.tripInfo.price || 0).toFixed(2)}</span>
                        </div>
                      </div>

                      <div className={styles.passengersSection}>
                        <h4 className={styles.passengerTitle}>Pasajeros</h4>
                        {passengers === null ? (
                          <p className={styles.loadingText}>Cargando pasajeros...</p>
                        ) : passengers.length > 0 ? (
                          <div className={styles.tableWrapper}>
                            <table className={styles.passengersTable}>
                              <thead>
                                <tr>
                                  <th>Asiento</th>
                                  <th>Nombre</th>
                                  <th>Identificación</th>
                                  <th>Correo</th>
                                  <th>Teléfono</th>
                                </tr>
                              </thead>
                              <tbody>
                                {passengers.map((p, idx) => (
                                  <tr key={idx}>
                                    <td>{p.seat_id}</td>
                                    <td>{p.nombre}</td>
                                    <td>{p.identificacion}</td>
                                    <td>{p.correo}</td>
                                    <td>{p.telefono}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className={styles.loadingText}>No hay información de pasajeros.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Modal editar */}
      {showEditModal && (
        <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && setShowEditModal(false)}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Editar perfil</h3>
              <button className={styles.modalClose} onClick={() => setShowEditModal(false)}>&times;</button>
            </div>
            <div className={styles.modalBody}>
              {[
                { label: 'Nombre', key: 'nombre', type: 'text', editable: true },
                { label: 'Apellido', key: 'apellido', type: 'text', editable: true },
                { label: 'Correo electrónico', key: 'correo', type: 'email', editable: false },
                { label: 'Cédula / Documento', key: 'identificacion', type: 'text', editable: false },
              ].map(({ label, key, type, editable }) => (
                <div key={key} className={styles.modalField}>
                  <label className={styles.modalLabel}>{label}</label>
                  <input
                    type={type}
                    className={`${styles.modalInput} ${!editable ? styles.modalInputDisabled : ''} ${editErrors[key] ? styles.modalInputError : ''}`}
                    value={editForm[key]}
                    onChange={(e) => editable && handleEditChange(key, e.target.value)}
                    disabled={!editable}
                    readOnly={!editable}
                  />
                  {editable && editErrors[key] && (
                    <span className={styles.errorText}>{editErrors[key]}</span>
                  )}
                  {!editable && (
                    <span className={styles.fieldHint}>Este campo no se puede editar.</span>
                  )}
                </div>
              ))}

              {/* Teléfono con indicativo */}
              <div className={styles.modalField}>
                <label className={styles.modalLabel}>Teléfono</label>
                <div className={styles.phoneContainer}>
                  <select
                    className={`${styles.modalInput} ${styles.phoneCodeSelect}`}
                    value={editForm.phoneCode || '+58'}
                    onChange={(e) => handleEditChange('phoneCode', e.target.value)}
                  >
                    <option value="+58">+58 Venezuela</option>
                    <option value="+57">+57 Colombia</option>
                    <option value="+1">+1 USA</option>
                  </select>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="Número de teléfono"
                    className={`${styles.modalInput} ${editErrors.phoneNumber ? styles.modalInputError : ''}`}
                    value={editForm.phoneNumber || ''}
                    onChange={(e) => handleEditChange('phoneNumber', e.target.value)}
                  />
                </div>
                {editErrors.phoneNumber && (
                  <span className={styles.errorText}>{editErrors.phoneNumber}</span>
                )}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelButton} onClick={() => setShowEditModal(false)}>Cancelar</button>
              <button
                className={styles.saveButton}
                onClick={saveProfileChanges}
                disabled={Object.keys(editErrors).length > 0}
              >
                Guardar cambios
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
